import { useState } from 'react';
import { X, Filter, ChevronDown, ChevronUp, Clock, User as UserIcon, Eye } from 'lucide-react';
import clsx from 'clsx';
import type { AuditLogEntry } from '../../api';
import { formatRelativeTime, getActionLabel, getActionIcon, getActionColor } from '../../utils/audit';

interface AuditLogPanelProps {
  isOpen: boolean;
  onClose: () => void;
  auditLogs: AuditLogEntry[];
  isLoading?: boolean;
}

/**
 * Pannello per visualizzare la cronologia delle modifiche al catalogo (Audit Log)
 *
 * Features:
 * - Lista cronologica delle modifiche ordinate per data (più recenti first)
 * - Filtro per tipo di azione (create, update, publish, delete)
 * - Ogni entry mostra: user, action, timestamp, details
 * - Expandable details section con JSON formattato
 * - Empty states per nessun log o nessun filtro match
 * - Performance: rendering < 200ms per 100 entries
 */
export function AuditLogPanel({ isOpen, onClose, auditLogs, isLoading }: AuditLogPanelProps) {
  const [filterAction, setFilterAction] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 25;

  // Filter logs
  const filteredLogs = auditLogs.filter((log) => {
    if (filterAction !== 'all' && log.action !== filterAction) return false;
    return true;
  });

  // Paginate
  const totalPages = Math.ceil(filteredLogs.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  // Stats
  const stats = {
    total: auditLogs.length,
    byAction: auditLogs.reduce((acc, log) => {
      acc[log.action] = (acc[log.action] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Clock className="w-5 h-5 text-purple-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Cronologia Modifiche</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {filteredLogs.length} di {auditLogs.length} registrazioni
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            aria-label="Chiudi pannello cronologia"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        {auditLogs.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtra per azione:</span>

              <select
                value={filterAction}
                onChange={(e) => {
                  setFilterAction(e.target.value);
                  setCurrentPage(1); // Reset to first page when filtering
                }}
                className="input-sm text-sm"
              >
                <option value="all">Tutte le azioni ({auditLogs.length})</option>
                <option value="catalog.create">
                  Creazione ({stats.byAction['catalog.create'] || 0})
                </option>
                <option value="catalog.update">
                  Modifica ({stats.byAction['catalog.update'] || 0})
                </option>
                <option value="catalog.publish">
                  Pubblicazione ({stats.byAction['catalog.publish'] || 0})
                </option>
                <option value="catalog.delete">
                  Eliminazione ({stats.byAction['catalog.delete'] || 0})
                </option>
              </select>

              {filterAction !== 'all' && (
                <button
                  onClick={() => {
                    setFilterAction('all');
                    setCurrentPage(1);
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Cancella filtro
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12 px-6">
              {auditLogs.length === 0 ? (
                <>
                  <Eye className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-lg font-medium text-gray-900">Nessuna registrazione</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Non ci sono ancora modifiche registrate per questo catalogo
                  </p>
                </>
              ) : (
                <>
                  <Filter className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-lg font-medium text-gray-900">Nessun risultato</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Nessuna modifica corrisponde al filtro selezionato
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {paginatedLogs.map((log) => (
                <AuditLogEntry key={log._id} log={log} />
              ))}
            </div>
          )}
        </div>

        {/* Footer with pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Pagina {currentPage} di {totalPages}
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn-sm btn-secondary"
              >
                Precedente
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="btn-sm btn-secondary"
              >
                Successiva
              </button>
            </div>
          </div>
        )}

        {/* Info footer */}
        {auditLogs.length > 0 && (
          <div className="px-6 py-2 bg-blue-50 border-t border-blue-100">
            <p className="text-xs text-blue-700">
              💡 Le registrazioni mostrano le ultime 100 modifiche al catalogo
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Single audit log entry component
 */
interface AuditLogEntryProps {
  log: AuditLogEntry;
}

function AuditLogEntry({ log }: AuditLogEntryProps) {
  const [showDetails, setShowDetails] = useState(false);

  const ActionIcon = getActionIcon(log.action);
  const actionColor = getActionColor(log.action);
  const actionLabel = getActionLabel(log.action);

  return (
    <div className="px-6 py-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start space-x-4">
        {/* Icon */}
        <div
          className={clsx(
            'flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center',
            actionColor
          )}
        >
          <ActionIcon className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Action & Version */}
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-900">{actionLabel}</span>
            {log.details?.version != null && (
              <span className="px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-700">
                v{String(log.details.version)}
              </span>
            )}
          </div>

          {/* User & Timestamp */}
          <div className="flex items-center space-x-3 mt-1 text-sm text-gray-600">
            <div className="flex items-center space-x-1.5">
              <UserIcon className="w-3.5 h-3.5" />
              <span>{log.userId?.name || 'Sistema'}</span>
            </div>
            <span className="text-gray-400">•</span>
            <span
              className="text-gray-500"
              title={new Date(log.createdAt).toLocaleString('it-IT')}
            >
              {formatRelativeTime(log.createdAt)}
            </span>
          </div>

          {/* Additional details (inline) */}
          {log.details && Object.keys(log.details).length > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              {log.details.copyFromVersion != null && (
                <span>Copiato da v{String(log.details.copyFromVersion)}</span>
              )}
              {log.details.previouslyPublishedVersion != null && (
                <span>Precedente: v{String(log.details.previouslyPublishedVersion)}</span>
              )}
              {Boolean(log.details.imported) && <span className="text-blue-600">Importato da JSON</span>}
              {Boolean(log.details.publishedImmediately) && (
                <span className="text-purple-600">Pubblicato immediatamente</span>
              )}
              {log.details.sectionsCount !== undefined && (
                <span>{String(log.details.sectionsCount)} sezioni</span>
              )}
            </div>
          )}

          {/* Expandable full details */}
          {showDetails && log.details && (
            <div className="mt-3 p-3 bg-gray-900 rounded-lg overflow-x-auto">
              <pre className="text-xs text-green-400 font-mono">
                {JSON.stringify(log.details, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Expand button */}
        {log.details && Object.keys(log.details).length > 0 && (
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            title={showDetails ? 'Nascondi dettagli' : 'Mostra dettagli'}
          >
            {showDetails ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
